/**
 * PGLP | MONTE HERMOM
 * COMPONENTE: ImovelParadigma_Documentos
 * VERSÃO: 2.0.0
 * ------------------------------------------------------------
 * NOVIDADES v2.0.0:
 *   ✔ Adiciona "Petição Inicial / Procuração" como obrigatório
 *   ✔ Mantém todo o resto (upload, validação, estado tri-modal)
 *
 * ESTADOS POR DOCUMENTO:
 *   vazio    → nenhum arquivo selecionado nem enviado
 *   pendente → PDF selecionado nesta sessão, ainda não persistido
 *   enviado  → já persistido no backend (retomada de fluxo)
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircle2,
  FileText,
  UploadCloud,
  X,
  ArrowRight,
  CloudUpload,
  Loader2,
  Calendar
} from 'lucide-react';

const TIPOS = [
  { chave: 'peticao',   titulo: 'Petição Inicial / Procuração',       obrigatorio: true  },
  { chave: 'matricula', titulo: 'Matrícula atualizada',               obrigatorio: true  },
  { chave: 'iptu',      titulo: 'IPTU / cadastro fiscal',             obrigatorio: true  },
  { chave: 'planta',    titulo: 'Planta aprovada',                    obrigatorio: false },
  { chave: 'habiteSe',  titulo: 'Habite-se / documento complementar', obrigatorio: false }
];

const LIMITE_BYTES = 20 * 1024 * 1024;

const STATUS = {
  VAZIO:    'vazio',
  PENDENTE: 'pendente',
  ENVIADO:  'enviado'
};

function estadoInicial(existentes = {}) {
  const base = {};
  for (const t of TIPOS) {
    const meta = existentes?.[t.chave];
    base[t.chave] = meta
      ? { status: STATUS.ENVIADO, file: null, meta }
      : { status: STATUS.VAZIO,   file: null, meta: null };
  }
  return base;
}

function formatarData(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  } catch { return ''; }
}

function tamanhoHumano(bytes) {
  if (!bytes && bytes !== 0) return '';
  const mb = bytes / 1024 / 1024;
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

export default function ImovelParadigma_Documentos({
  reportNumber,
  documentosExistentes = {},
  onBack,
  onComplete
}) {
  const [docs, setDocs] = useState(() => estadoInicial(documentosExistentes));
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [carregandoRemotos, setCarregandoRemotos] = useState(false);

  const inputRefs = useRef({});

  useEffect(() => {
    const jaTemDados = Object.keys(documentosExistentes || {}).length > 0;
    if (jaTemDados || !reportNumber) return;

    let cancelado = false;
    (async () => {
      try {
        setCarregandoRemotos(true);
        const resp = await fetch(`/api/laudos/${reportNumber}/documentos`);
        if (!resp.ok) return;
        const dados = await resp.json();
        if (cancelado) return;
        setDocs(estadoInicial(dados || {}));
      } catch {
        /* silencioso */
      } finally {
        if (!cancelado) setCarregandoRemotos(false);
      }
    })();
    return () => { cancelado = true; };
  }, [reportNumber, documentosExistentes]);

  const resumo = useMemo(() => {
    const pendentes = Object.values(docs).filter(d => d.status === STATUS.PENDENTE).length;
    const enviados  = Object.values(docs).filter(d => d.status === STATUS.ENVIADO).length;
    return { pendentes, enviados, total: pendentes + enviados };
  }, [docs]);

  const obrigatoriosOk = useMemo(() => {
    return TIPOS
      .filter(t => t.obrigatorio)
      .every(t => docs[t.chave]?.status !== STATUS.VAZIO);
  }, [docs]);

  const selecionar = (chave, file) => {
    if (!file) return;
    setErro('');

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setErro('Somente arquivos PDF são aceitos nesta etapa.');
      return;
    }
    if (file.size > LIMITE_BYTES) {
      setErro('O arquivo excede o limite de 20 MB.');
      return;
    }

    setDocs(prev => ({
      ...prev,
      [chave]: { status: STATUS.PENDENTE, file, meta: prev[chave]?.meta || null }
    }));
  };

  const remover = (chave) => {
    setDocs(prev => ({
      ...prev,
      [chave]: { status: STATUS.VAZIO, file: null, meta: null }
    }));
    if (inputRefs.current[chave]) inputRefs.current[chave].value = '';
  };

  const concluir = async () => {
    if (!obrigatoriosOk) {
      setErro('Petição, Matrícula e IPTU são obrigatórios para continuar.');
      return;
    }
    setEnviando(true);
    try {
      if (typeof onComplete === 'function') {
        await onComplete({
          reportNumber: reportNumber || null,
          documentos: docs,
          arquivos: Object.fromEntries(
            Object.entries(docs)
              .filter(([, v]) => v.file)
              .map(([k, v]) => [k, v.file])
          ),
          resumo
        });
      }
    } catch (e) {
      setErro('Não foi possível concluir. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  const voltar = () => {
    if (resumo.pendentes > 0 && !enviando) {
      const ok = window.confirm(
        'Existem PDFs selecionados que ainda não foram inseridos no fluxo. Deseja realmente voltar?'
      );
      if (!ok) return;
    }
    if (typeof onBack === 'function') onBack();
  };

  return (
    <div className="w-full min-h-screen bg-slate-950 p-8 lg:p-12">
      <div className="max-w-4xl mx-auto">

        {/* HEADER */}
        <div className="bg-slate-900 rounded-[4rem] p-10 border-[12px] border-slate-900 shadow-2xl flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-black text-white uppercase italic tracking-tighter">Monte Hermom</h1>
            <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest mt-1">
              Cofre documental do imóvel
            </p>
          </div>
          <div className="bg-white/5 px-6 py-2 rounded-full border border-white/10 flex items-center gap-2">
            <span className="text-[10px] font-bold text-slate-500 uppercase">Laudo:</span>
            <span className="font-mono font-bold text-amber-400">{reportNumber || 'não informado'}</span>
          </div>
        </div>

        {/* TÍTULO SEÇÃO */}
        <div className="flex items-center justify-between mb-8 px-4 flex-wrap gap-3">
          <div>
            <h2 className="uppercase tracking-[0.2em] text-sm font-black text-white">Documentos reais do imóvel</h2>
            <p className="text-xs text-slate-500 mt-2">
              Selecione os PDFs recebidos. A análise/OCR deve ocorrer em etapa própria.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {carregandoRemotos && (
              <span className="flex items-center gap-2 text-[10px] uppercase font-black tracking-widest text-slate-400">
                <Loader2 size={12} className="animate-spin" /> Sincronizando
              </span>
            )}
            {resumo.enviados > 0 && (
              <span className="text-[10px] font-black uppercase tracking-widest text-sky-400">
                {resumo.enviados} já enviado(s)
              </span>
            )}
            {resumo.pendentes > 0 && (
              <span className="text-[10px] font-black uppercase tracking-widest text-amber-400">
                {resumo.pendentes} pendente(s)
              </span>
            )}
            {resumo.total === 0 && (
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">
                0 arquivo(s)
              </span>
            )}
          </div>
        </div>

        {/* LISTA */}
        <div className="space-y-4">
          {TIPOS.map((item) => {
            const estado = docs[item.chave];
            const isVazio    = estado.status === STATUS.VAZIO;
            const isPendente = estado.status === STATUS.PENDENTE;
            const isEnviado  = estado.status === STATUS.ENVIADO;

            const borda = isEnviado
              ? 'border-sky-500/50 bg-sky-500/5'
              : isPendente
                ? 'border-emerald-500/50 bg-emerald-500/5'
                : 'border-white/10 bg-slate-900/50';

            const iconeFundo = isEnviado
              ? 'bg-sky-500/10'
              : isPendente
                ? 'bg-emerald-500/10'
                : 'bg-white/5';

            const subTexto = isEnviado
              ? `${estado.meta?.nome || 'arquivo.pdf'} • enviado em ${formatarData(estado.meta?.enviadoEm)}`
              : isPendente
                ? `${estado.file.name} • ${tamanhoHumano(estado.file.size)}`
                : 'Nenhum arquivo selecionado';

            const subCor = isEnviado
              ? 'text-sky-300/80'
              : isPendente
                ? 'text-emerald-300/80'
                : 'text-slate-500';

            return (
              <div
                key={item.chave}
                className={`p-6 border-2 border-dashed rounded-2xl transition-all ${borda}`}
              >
                <div className="flex items-center justify-between gap-6 flex-wrap">
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${iconeFundo}`}>
                      {isEnviado  && <CloudUpload   className="text-sky-400"      size={21} />}
                      {isPendente && <CheckCircle2 className="text-emerald-500"  size={21} />}
                      {isVazio    && <FileText     className="text-slate-500"    size={21} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-white">{item.titulo}</p>
                        {item.obrigatorio && (
                          <span className="text-[9px] uppercase text-amber-400 font-black">Obrigatório</span>
                        )}
                        {isEnviado && (
                          <span className="text-[9px] uppercase bg-sky-500/20 text-sky-300 px-2 py-0.5 rounded-full font-black tracking-widest">
                            Já enviado
                          </span>
                        )}
                        {isPendente && (
                          <span className="text-[9px] uppercase bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full font-black tracking-widest">
                            Pendente
                          </span>
                        )}
                      </div>
                      <p className={`text-[10px] uppercase truncate mt-1 ${subCor}`}>
                        {subTexto}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <input
                      ref={(el) => { inputRefs.current[item.chave] = el; }}
                      type="file"
                      accept="application/pdf,.pdf"
                      className="hidden"
                      onChange={(e) => selecionar(item.chave, e.target.files?.[0] || null)}
                    />
                    <button
                      type="button"
                      onClick={() => inputRefs.current[item.chave]?.click()}
                      className="bg-amber-400 text-slate-950 px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-white transition-colors flex items-center gap-2"
                    >
                      <UploadCloud size={14} />
                      {isPendente ? 'Trocar PDF' : isEnviado ? 'Substituir' : 'Selecionar PDF'}
                    </button>
                    {!isVazio && (
                      <button
                        type="button"
                        onClick={() => remover(item.chave)}
                        className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-red-500 hover:border-red-500/30 flex items-center justify-center transition-colors"
                        title={isEnviado ? 'Remover referência ao arquivo enviado' : 'Remover arquivo'}
                      >
                        <X size={15} />
                      </button>
                    )}
                    {isEnviado && estado.meta?.url && (
                      <a
                        href={estado.meta.url}
                        target="_blank"
                        rel="noreferrer"
                        className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-sky-400 hover:bg-sky-500/10 flex items-center justify-center transition-colors"
                        title="Abrir arquivo enviado"
                      >
                        <Calendar size={15} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ERRO */}
        {erro && (
          <div className="mt-6 p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold">
            {erro}
          </div>
        )}

        {/* AÇÕES */}
        <div className="mt-10 flex justify-between gap-4">
          <button
            type="button"
            onClick={voltar}
            disabled={enviando}
            className="px-7 py-4 rounded-2xl bg-white/5 border border-white/10 text-white font-black uppercase tracking-widest text-[10px] hover:bg-white/10 transition-all disabled:opacity-50"
          >
            Voltar
          </button>
          <button
            type="button"
            onClick={concluir}
            disabled={!obrigatoriosOk || enviando}
            className="px-8 py-4 rounded-2xl bg-amber-400 text-slate-950 font-black uppercase tracking-widest text-[10px] disabled:bg-slate-800 disabled:text-slate-600 disabled:cursor-not-allowed hover:bg-white transition-all flex items-center gap-3"
          >
            {enviando ? (
              <>
                <Loader2 size={15} className="animate-spin" /> Inserindo...
              </>
            ) : (
              <>
                Inserir documentos no fluxo <ArrowRight size={15} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}